import React, { useState, useEffect, useCallback } from 'react'
import { getMoscowTasks, getMoscowPipeline, refreshMoscowFileUrl } from '../lib/api'

const PHASE_NAMES = {
  1: 'Заезд и обустройство',
  2: 'Документальный блок',
  3: 'Регистрация и идентификация',
  4: 'Связь и финансы',
  5: 'Отправка',
}

const PHASE_ICONS = { 1: '🏠', 2: '📋', 3: '🪪', 4: '📱', 5: '✈️' }

function fmtDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch { return '' }
}

function fileExt(name = '') {
  const ext = name.split('.').pop().toLowerCase()
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext)) return 'image'
  if (ext === 'pdf') return 'pdf'
  return 'file'
}

function fileIcon(name) {
  const t = fileExt(name)
  if (t === 'image') return '🖼'
  if (t === 'pdf') return '📄'
  return '📎'
}

function DocCard({ file, label, sublabel }) {
  const [loading, setLoading] = useState(false)

  async function handleOpen(e) {
    e.preventDefault()
    if (!file.path) { window.open(file.url, '_blank', 'noreferrer'); return }
    setLoading(true)
    try {
      const url = await refreshMoscowFileUrl(file.path)
      window.open(url, '_blank', 'noreferrer')
    } catch {
      window.open(file.url, '_blank', 'noreferrer')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="moscow-docs-row" style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px', borderRadius: 8,
      background: '#fafaf8', border: '1px solid #efefec',
      marginBottom: 6,
    }}>
      <span style={{ fontSize: 20, flexShrink: 0 }}>{fileIcon(file.name)}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.name}
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>
          {label}{sublabel ? ' · ' + sublabel : ''}{file.uploaded_at ? ' · ' + fmtDate(file.uploaded_at) : ''}
        </div>
      </div>
      <a href={file.url} onClick={handleOpen}
        style={{ fontSize: 12, color: loading ? '#aaa' : '#185FA5', textDecoration: 'none', flexShrink: 0, padding: '4px 8px', background: '#EDF4FC', borderRadius: 6 }}>
        {loading ? '...' : 'Открыть'}
      </a>
    </div>
  )
}

function Section({ title, icon, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  if (count === 0) return null
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        onClick={() => setOpen(o => !o)}
        className="moscow-docs-section-toggle"
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: '#f5f5f3', cursor: 'pointer', userSelect: 'none', marginBottom: open ? 8 : 0 }}
      >
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', flex: 1 }}>{title}</span>
        <span className="moscow-docs-count" style={{ fontSize: 11, color: '#888', background: 'white', borderRadius: 10, padding: '1px 7px' }}>{count}</span>
        <span style={{ fontSize: 11, color: '#aaa' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ paddingLeft: 4 }}>{children}</div>}
    </div>
  )
}

export default function MoscowDocuments({ candidate }) {
  const [tasks, setTasks] = useState([])
  const [pipeline, setPipeline] = useState([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const [allTasks, rows] = await Promise.all([
        getMoscowTasks(),
        getMoscowPipeline(candidate.id),
      ])
      setTasks(allTasks)
      setPipeline(rows)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [candidate.id])

  useEffect(() => { load() }, [load])

  // Файлы из moscow_pipeline, сгруппированные по фазе
  const pipelineMap = {}
  for (const row of pipeline) pipelineMap[row.task_id] = row

  const byPhase = {}
  for (const task of tasks) {
    const row = pipelineMap[task.id]
    const files = row?.files || []
    // Для накопительных задач — файлы внутри expense_entries
    const entryFiles = (row?.expense_entries || [])
      .filter(e => e.file)
      .map(e => ({ ...e.file, _entryNote: e.note, _entryDate: e.date }))
    const allFiles = [...files, ...entryFiles]
    if (allFiles.length === 0) continue
    if (!byPhase[task.phase]) byPhase[task.phase] = []
    byPhase[task.phase].push({ task, files: allFiles })
  }

  // Из основной карточки кандидата
  const passportPhotos = Array.isArray(candidate.passport_photos) ? candidate.passport_photos : []
  const visas = Array.isArray(candidate.visas) ? candidate.visas : []
  const visaFiles = visas.flatMap(v => (v.files || []).map(f => ({ ...f, _visaName: v.name })))

  const totalCount =
    passportPhotos.length +
    visas.length +
    Object.values(byPhase).reduce((s, arr) => s + arr.reduce((s2, g) => s2 + g.files.length, 0), 0)

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: 13 }}>Загрузка документов...</div>
  )

  if (totalCount === 0) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#ccc', fontSize: 13 }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
      Документы ещё не загружены
    </div>
  )

  return (
    <div>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f0f0ec' }}>
        Всего документов: <strong style={{ color: '#1a1a1a' }}>{totalCount}</strong>
      </div>

      {/* Паспорт */}
      {passportPhotos.length > 0 && (
        <Section title="Фото паспорта" icon="🛂" count={passportPhotos.length} defaultOpen={true}>
          {passportPhotos.map((f, i) => (
            <DocCard key={i} file={f} label="Паспорт" />
          ))}
        </Section>
      )}

      {/* Визы */}
      {visas.length > 0 && (
        <Section title="Визы" icon="🛂" count={visas.length} defaultOpen={true}>
          {visas.map((v, i) => (
            <div key={i} style={{ background: '#fafaf8', border: '1px solid #efefec', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>🛂 {v.name || 'Виза ' + (i + 1)}</div>
              {v.submission_date && (
                <div style={{ fontSize: 12, color: '#555', marginBottom: 3 }}>
                  Дата подачи: {v.submission_date.split('-').reverse().join('.')}
                </div>
              )}
              {v.application_number && (
                <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
                  № заявки: {v.application_number}
                </div>
              )}
              {(v.files || []).map((f, fi) => (
                <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#185FA5', textDecoration: 'none' }}>📄 {f.name}</a>
                </div>
              ))}
              {(!v.submission_date && !v.application_number && (!v.files || v.files.length === 0)) && (
                <div style={{ fontSize: 12, color: '#ccc' }}>Нет данных</div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* Документы по фазам пайплайна */}
      {[1, 2, 3, 4, 5].map(phase => {
        const groups = byPhase[phase]
        if (!groups) return null
        const phaseCount = groups.reduce((s, g) => s + g.files.length, 0)
        return (
          <Section key={phase} title={PHASE_NAMES[phase]} icon={PHASE_ICONS[phase]} count={phaseCount}>
            {groups.map(({ task, files }) => (
              <div key={task.id} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4, paddingLeft: 2 }}>
                  {task.name}
                </div>
                {files.map((f, i) => (
                  <DocCard
                    key={i}
                    file={f}
                    label={task.file_label || task.name}
                    sublabel={f._entryNote || (f._entryDate ? fmtDate(f._entryDate) : null)}
                  />
                ))}
              </div>
            ))}
          </Section>
        )
      })}
    </div>
  )
}
